import type { InvivoLegacyNodeMaterialInputValue, InvivoSceneTexturePlan } from "../../types/invivo_scene";

function buildTexture(BABYLON: any, scene: unknown, texturePlan?: InvivoSceneTexturePlan): unknown {
  if (!texturePlan) {
    return undefined;
  }

  const texture = new BABYLON.Texture(texturePlan.url, scene, false, texturePlan.invertY);
  texture.uScale = texturePlan.uScale;
  texture.vScale = texturePlan.vScale;

  return texture;
}

function runtimeInputValue(BABYLON: any, value: InvivoLegacyNodeMaterialInputValue): unknown {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    if (/^#[0-9a-f]{6}$/i.test(value)) {
      return BABYLON.Color3.FromHexString(value);
    }

    throw new Error(`Unsupported legacy node material string input "${value}".`);
  }

  if (value && typeof value === "object" && "url" in value) {
    throw new Error("Texture values must use textureBindings, not nodeMaterialInputs.");
  }

  if (value && typeof value === "object") {
    return value;
  }

  throw new Error("Unsupported legacy node material input value.");
}

export function createLegacyNodeMaterial({
  scene,
  serializedMaterial,
  materialName,
  backFaceCulling,
  alpha = 1,
  transparencyMode,
  nodeMaterialInputs,
  textureBindings,
}: {
  scene: unknown;
  serializedMaterial: Record<string, unknown>;
  materialName: string;
  backFaceCulling: boolean;
  alpha?: number;
  transparencyMode?: "opaque" | "alpha_blend" | "inherit";
  nodeMaterialInputs?: Record<string, InvivoLegacyNodeMaterialInputValue>;
  textureBindings?: Record<string, InvivoSceneTexturePlan | undefined>;
}): unknown {
  const { BABYLON } = window;

  if (!BABYLON.NodeMaterial) {
    throw new Error("Babylon NodeMaterial is required for legacy node materials.");
  }

  const material = new BABYLON.NodeMaterial(materialName, scene);
  material.parseSerializedObject(serializedMaterial);
  material.backFaceCulling = backFaceCulling;
  material.alpha = alpha;
  if (transparencyMode === "alpha_blend") {
    material.transparencyMode = BABYLON.Material?.MATERIAL_ALPHABLEND ?? 2;
  } else if (transparencyMode === "opaque") {
    material.transparencyMode = BABYLON.Material?.MATERIAL_OPAQUE ?? 0;
  }

  material.getInputBlocks().forEach((input: { name: string; value?: unknown }) => {
    const value = nodeMaterialInputs?.[input.name];

    if (value === undefined) {
      return;
    }

    const runtimeValue = runtimeInputValue(BABYLON, value);

    if (runtimeValue && typeof runtimeValue === "object" && input.value && typeof input.value === "object") {
      Object.assign(input.value as Record<string, unknown>, runtimeValue as Record<string, unknown>);
      return;
    }

    input.value = runtimeValue;
  });

  material.getAllTextureBlocks().forEach((input: { name: string; texture?: unknown }) => {
    const texture = buildTexture(BABYLON, scene, textureBindings?.[input.name]);

    if (texture !== undefined) {
      input.texture = texture;
    }
  });

  material.build();

  return material;
}
